/**
 * Outreach v2 — shared constants (client mirror of outreach.constants.js).
 *
 * Safe per-account caps (§11) ship ON. These values must stay in lockstep with
 * the backend `DEFAULT_CAPS` / `DEFAULT_SEND_WINDOW` / `SAFE_CAP_CEILING` so the
 * "Safe / Aggressive" hints and the raise-cap warning match what the server
 * actually enforces (the service clamps anything above the ceiling).
 */

import type {
    AccountCaps,
    AccountProvider,
    AccountStatus,
    CampaignGoal,
    CampaignStatus,
    SendWindow,
    InboxFilter,
    InterestLevel,
    ConversationStatus,
    ReplyClass,
} from './types';
import type { BadgeVariant } from '@/components/ui-v2';

/* ─── Safe defaults (§11) ─── */
export const DEFAULT_CAPS: Record<
    'email' | 'linkedin_message' | 'linkedin_invite' | 'linkedin_inmail' | 'whatsapp',
    AccountCaps
> = {
    email: { daily: 80, hourly: 15, minDelaySeconds: 45, jitter: 75 },
    linkedin_message: { daily: 60, hourly: 12, minDelaySeconds: 45, jitter: 75 },
    linkedin_invite: { daily: 20, hourly: 5, minDelaySeconds: 60, jitter: 120 },
    linkedin_inmail: { daily: 20, hourly: 5, minDelaySeconds: 60, jitter: 120 },
    whatsapp: { daily: 60, hourly: 12, minDelaySeconds: 45, jitter: 75 },
};

/** Mon–Fri business hours (0 = Sunday … 6 = Saturday). */
export const DEFAULT_SEND_WINDOW: SendWindow = {
    tz: 'UTC',
    days: [1, 2, 3, 4, 5],
    start: '08:00',
    end: '18:00',
};

/** Hard ceiling; raising above it is reserved for `outreach.admin`. The UI warns
 *  (never blocks) as a cap approaches/exceeds the safe zone. */
export const SAFE_CAP_CEILING: Record<
    'email' | 'linkedin_message' | 'linkedin_invite' | 'linkedin_inmail' | 'whatsapp',
    { daily: number; hourly: number }
> = {
    email: { daily: 200, hourly: 40 },
    linkedin_message: { daily: 100, hourly: 20 },
    linkedin_invite: { daily: 30, hourly: 8 },
    linkedin_inmail: { daily: 30, hourly: 8 },
    whatsapp: { daily: 100, hourly: 20 },
};

/** The cap profile key for an account: email + WhatsApp accounts use their single
 *  caps profile; LinkedIn accounts use message caps for `caps` (invites use the
 *  invite profile). */
export type CapProfileKey = 'email' | 'linkedin_message' | 'linkedin_invite' | 'linkedin_inmail' | 'whatsapp';

/* ─── Status display (semantic tokens, §16) ─── */
export const STATUS_META: Record<AccountStatus, { label: string; token: string; bg: string; description: string }> = {
    pending: {
        label: 'Finishing sign-in',
        token: 'var(--text-tertiary)',
        bg: 'var(--surface-tertiary)',
        description: 'Complete sign-in in the secure window to activate this account.',
    },
    connected: {
        label: 'Connected',
        token: 'var(--fg-success)',
        bg: 'var(--bg-success)',
        description: 'Sending normally.',
    },
    warming: {
        label: 'Warming up',
        token: 'var(--fg-warning)',
        bg: 'var(--bg-warning)',
        description: 'Ramping volume low → full over ~2 weeks to protect deliverability.',
    },
    limited: {
        label: 'Rate limited',
        token: 'var(--fg-warning)',
        bg: 'var(--bg-warning)',
        description: 'Provider flagged this account — sends paused, other accounts keep going.',
    },
    disconnected: {
        label: 'Disconnected',
        token: 'var(--text-tertiary)',
        bg: 'var(--surface-tertiary)',
        description: 'Reconnect to resume sending from this account.',
    },
    error: {
        label: 'Error',
        token: 'var(--fg-danger)',
        bg: 'var(--bg-danger)',
        description: 'Something went wrong with this account.',
    },
    on_hold: {
        label: 'On hold',
        token: 'var(--fg-warning)',
        bg: 'var(--bg-warning)',
        description: 'Connected in another workspace. Disconnect it there to use it here.',
    },
};

export const PROVIDER_META: Record<AccountProvider, { label: string }> = {
    gmail: { label: 'Gmail' },
    outlook: { label: 'Outlook' },
    imap: { label: 'IMAP' },
    linkedin: { label: 'LinkedIn' },
    whatsapp: { label: 'WhatsApp' },
};

/* ─── Provider error codes → human copy (Stage 1) ─── *
 * `account.lastError` often carries a raw Unipile status token (CREDENTIALS,
 * SYNC_ERROR, …) which means nothing to a recruiter. Map the known codes to an
 * actionable sentence; anything already human-readable (server-written
 * sentences like "This account was moved to another workspace.") passes
 * through untouched. Keep the raw code in the hover title for debugging.
 */
const ACCOUNT_ERROR_HELP: Record<string, string> = {
    CREATION_FAIL: "Sign-in didn't finish — click Reconnect to try again.",
    CREDENTIALS: 'Sign-in expired or was revoked — click Reconnect to sign in again.',
    DISCONNECTED: 'The provider disconnected this account — reconnect to resume sending.',
    STOPPED: 'The provider stopped this account — reconnect to resume sending.',
    SYNC_ERROR: 'The provider hit a sync error — try Reconnect; if it persists, remove and re-add the account.',
    ERROR: 'The provider reported an error — try Reconnect; if it persists, remove and re-add the account.',
    DELETED: 'The connection was removed on the provider side — reconnect to restore it.',
    LIMITED: 'The provider rate-limited this account — sending is paused until it recovers.',
    RATE_LIMIT: 'The provider rate-limited this account — sending is paused until it recovers.',
};

/** Human-readable version of `account.lastError` (raw codes → actionable copy). */
export function humanizeAccountError(lastError: string | null | undefined): string | null {
    if (!lastError) return null;
    return ACCOUNT_ERROR_HELP[lastError.trim().toUpperCase()] ?? lastError;
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CAMPAIGN_GOAL_LABELS: Record<CampaignGoal, string> = {
    book_call: 'Book a call',
    invite_apply: 'Invite to apply',
    request_cv: 'Request CV',
    confirm_interest: 'Confirm interest',
    interview_invite: 'Interview invite',
    start_qualification: 'Start qualification',
    talent_pool: 'Talent pool',
};

/** Campaign status chip (§4 Job Detail badge + Plan-Mode panel header). */
export const CAMPAIGN_STATUS_META: Record<CampaignStatus, { label: string; variant: BadgeVariant }> = {
    draft: { label: 'Draft', variant: 'default' },
    ready: { label: 'Ready', variant: 'info' },
    active: { label: 'Active', variant: 'success' },
    paused: { label: 'Paused', variant: 'warning' },
    completed: { label: 'Completed', variant: 'info' },
    archived: { label: 'Archived', variant: 'default' },
};

/* ─── CRM inbox (§9) ─── */

/** Named quick-filter chips above the inbox list. `needs_action` is the default:
 *  unanswered replies + escalations — everything waiting on a human. (The server
 *  also accepts `interested`/`snoozed`, not surfaced as chips.) */
export const INBOX_FILTERS: { id: InboxFilter; label: string }[] = [
    { id: 'needs_action', label: 'Needs action' },
    { id: 'all', label: 'All inbox' },
];

/** Interest rollup marker (§9 triage) — the lemlist-style hot/warm/dead read on
 *  a thread. Dot colours stay semantic; `clear` is the "remove my mark" row. */
export const INTEREST_META: Record<InterestLevel, { label: string; variant: BadgeVariant; dot: string }> = {
    interested: { label: 'Interested', variant: 'success', dot: 'var(--fg-success, #1a7f37)' },
    maybe: { label: 'Maybe', variant: 'warning', dot: 'var(--fg-warning, #9a6700)' },
    not_interested: { label: 'Not interested', variant: 'default', dot: 'var(--text-tertiary)' },
};

export const INTEREST_ORDER: InterestLevel[] = ['interested', 'maybe', 'not_interested'];

/** Candidate status set (§9) for the context-pane dropdown. */
export const CONVERSATION_STATUS_LABELS: Record<ConversationStatus, string> = {
    new: 'New',
    contacted: 'Contacted',
    opened: 'Opened',
    replied: 'Replied',
    interested: 'Interested',
    maybe: 'Maybe',
    not_interested: 'Not interested',
    meeting_booked: 'Meeting booked',
    cv_requested: 'CV requested',
    cv_received: 'CV received',
    moved_to_pipeline: 'Moved to pipeline',
    archived: 'Archived',
    suppressed: 'Suppressed',
};

export const CONVERSATION_STATUS_ORDER: ConversationStatus[] = [
    'new',
    'contacted',
    'opened',
    'replied',
    'interested',
    'maybe',
    'not_interested',
    'meeting_booked',
    'cv_requested',
    'cv_received',
    'moved_to_pipeline',
    'archived',
    'suppressed',
];

/** AI reply classification badge (§8). Copy stays human-readable; the colour maps
 *  to the Lantern semantic badge variants. The AI that sets these lands in B4. */
export const REPLY_CLASS_META: Record<ReplyClass, { label: string; variant: BadgeVariant }> = {
    interested: { label: 'Interested', variant: 'success' },
    maybe: { label: 'Maybe', variant: 'warning' },
    not_interested: { label: 'Not interested', variant: 'default' },
    unsubscribe: { label: 'Unsubscribe', variant: 'danger' },
    out_of_office: { label: 'Out of office', variant: 'info' },
    wrong_person: { label: 'Wrong person', variant: 'default' },
    asks_job_details: { label: 'Asks job details', variant: 'info' },
    asks_compensation: { label: 'Asks compensation', variant: 'warning' },
    asks_remote: { label: 'Asks remote', variant: 'info' },
    asks_visa: { label: 'Asks visa', variant: 'warning' },
    asks_schedule: { label: 'Asks to schedule', variant: 'accent' },
    already_applied: { label: 'Already applied', variant: 'info' },
    referred_someone: { label: 'Referred someone', variant: 'info' },
    unclear: { label: 'Unclear', variant: 'default' },
    negative_escalate: { label: 'Escalate', variant: 'danger' },
};

/** Semantic badge variant for a candidate status chip. */
export const CONVERSATION_STATUS_VARIANT: Record<ConversationStatus, BadgeVariant> = {
    new: 'default',
    contacted: 'default',
    opened: 'info',
    replied: 'info',
    interested: 'success',
    maybe: 'warning',
    not_interested: 'default',
    meeting_booked: 'success',
    cv_requested: 'accent',
    cv_received: 'success',
    moved_to_pipeline: 'success',
    archived: 'default',
    suppressed: 'danger',
};

/** Common IANA timezones for the send-window picker. */
export const TIMEZONE_OPTIONS = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Singapore',
    'Asia/Kolkata',
    'Asia/Tokyo',
    'Australia/Sydney',
];
